import { useDataStore, useFilterStore } from "@/stores";
import clsx from "clsx";
import { forwardRef, useImperativeHandle } from "react";

interface DisciplineFilterProps {
    shouldConfirm?: boolean;
    small?: boolean;
}

export interface DisciplineFilterRef {
    confirmChange: () => void;
}

const DisciplineFilter = forwardRef<DisciplineFilterRef, DisciplineFilterProps>(({ shouldConfirm, small }, ref) => {
    const disciplines = useDataStore((state) => state.disciplines);
    const activeVersion = useFilterStore((state) => state.activeModalVersion);
    const activeDisciplines = useFilterStore((state) => state.activeDisciplines);
    const setActiveDisciplines = useFilterStore((state) => state.setActiveDisciplines);
    const unconfirmedDisciplines = useFilterStore((state) => state.unconfirmedDisciplines);
    const setUnconfirmedDisciplines = useFilterStore((state) => state.setUnconfirmedDisciplines);

    useImperativeHandle(ref, () => ({
        confirmChange: () => {
            setActiveDisciplines(unconfirmedDisciplines);
        },
    }));

    if (!disciplines[activeVersion!]) return null;

    return disciplines[activeVersion!]
        .sort((a, b) => a.code.localeCompare(b.code))
        .map((discipline) => {
            const id = `discipline-${shouldConfirm ? "confirm" : "direct"}-${discipline.id}`;
            const isActive = activeDisciplines.includes(discipline.id);
            const isUnconfirmed = unconfirmedDisciplines.includes(discipline.id);
            const isSelected = shouldConfirm ? isUnconfirmed : isActive;

            const handleClick = () => {
                if (shouldConfirm) {
                    const currentDisciplines = isUnconfirmed
                        ? unconfirmedDisciplines.filter((id) => id !== discipline.id)
                        : [...unconfirmedDisciplines, discipline.id];

                    setUnconfirmedDisciplines(currentDisciplines);
                } else {
                    const currentDisciplines = isActive
                        ? activeDisciplines.filter((id) => id !== discipline.id)
                        : [...activeDisciplines, discipline.id];

                    setActiveDisciplines(currentDisciplines);
                }
            };

            return (
                <div key={id} className="group">
                    <div
                        className={clsx([
                            "group flex gap-3 p-5 ",
                            small ? "h-[56px] px-5 py-2" : "h-[90px] p-5",
                            "rounded-md bg-primary-100 text-primary-500 group-has-checked:bg-primary-500 group-has-checked:text-white text-base font-medium items-center select-none cursor-pointer hover:bg-primary-600 hover:text-white",
                        ])}
                        onClick={handleClick}
                    >
                        <div className="group grid size-6 grid-cols-1 shrink-0">
                            <input
                                id={id}
                                name={id}
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                className={clsx([
                                    "col-start-1 row-start-1 appearance-none rounded-lg border-2 min-w-6 min-h-6",
                                    "border-primary-300 bg-primary-100",
                                    "checked:bg-white checked:outline-2 checked:-outline-offset-4 checked:outline-primary-500 cursor-pointer",
                                ])}
                            />
                            <svg
                                fill="none"
                                viewBox="0 0 14 14"
                                className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-primary-500 group-has-disabled:stroke-gray-950/25"
                            >
                                <path
                                    d="M3 8L6 11L11 3.5"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="opacity-0 group-has-checked:opacity-100"
                                />
                                <path
                                    d="M3 7H11"
                                    strokeWidth={2}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="opacity-0 group-has-indeterminate:opacity-100"
                                />
                            </svg>
                        </div>
                        {discipline.svgIcon ? (
                            <div
                                className={clsx([small ? "h-[37px]" : "h-[52px]", "flex-shrink-0 aspect-square"])}
                                dangerouslySetInnerHTML={{ __html: discipline.svgIcon }}
                            />
                        ) : (
                            discipline.icon && (
                                <img
                                    src={discipline.icon}
                                    alt="icon"
                                    className={clsx([
                                        small ? "h-[37px] w-[37px]" : "h-[52px] w-[52px]",
                                        "flex-shrink-0",
                                    ])}
                                />
                            )
                        )}
                        {discipline.label}
                    </div>
                </div>
            );
        });
});

export default DisciplineFilter;
